// src/oura/tasks/sample.ts
//
// A made-up Master Tracker for the fixture client, shaped like the sheets Angel
// builds (P0 access and baseline, then one phase per month). Sample data for
// the Tasks view until the tasks route ships.

import type { Task, TaskPhase } from "./model";

export const SAMPLE_SHEET_URL = "https://docs.google.com/spreadsheets/";

const US = "ourteam";
const CLIENT = "Client";
const BOTH = "ourteam + Client";

function t(id: string, n: number, owner: string, target: string, status: string, action: string, gate: string, result = "", tool = "", notes = ""): Task {
    return { id, n, owner, target, status, action, gate, result, tool, notes };
}

export const SAMPLE_PHASES: TaskPhase[] = [
    {
        key: "P0",
        name: "P0 Access and Baseline",
        title: "P0 Access and Baseline - Access in hand, starting point recorded",
        sub: "Access by Wed 30 Sep 2026, baseline Fri 2 Oct 2026. Your website login is the one thing that can move the rest.",
        tasks: [
            t(
                "p0-1",
                1,
                CLIENT,
                "30 Sep 2026",
                "Done",
                "Website login: Administrator access for aeo@ourteam.ai (checklist 1)",
                "aeo@ourteam.ai signs in with the Administrator role.",
                "29 Sep: signed in as Administrator.",
                "Website admin",
            ),
            t(
                "p0-2",
                2,
                CLIENT,
                "30 Sep 2026",
                "Done",
                "Search Console: add aeo@ourteam.ai with Full permission (checklist 2)",
                "Property visible to aeo@ourteam.ai with Full permission.",
                "30 Sep: Full permission granted.",
                "Search Console",
            ),
            t(
                "p0-3",
                3,
                CLIENT,
                "30 Sep 2026",
                "Done",
                "Google listing: Manager access to the head office listing (checklist 3)",
                "aeo@ourteam.ai listed as Manager on the head office listing.",
                "30 Sep: Manager access accepted.",
                "Google Business Profile",
            ),
            t("p0-4", 4, US, "2 Oct 2026", "Done", "Full-site backup before any edit", "Backup downloaded, restorable, date logged in Result Record.", "2 Oct: backup saved to the Project folder."),
            t(
                "p0-5",
                5,
                US,
                "2 Oct 2026",
                "Done",
                "A1 Score the 50 questions as the baseline: 3 asks each on ChatGPT and Gemini (AI reading 1 of 6)",
                "300 asks per engine logged; mention and first-named rate recorded as a range per engine.",
                "2 Oct: baseline range recorded per engine.",
                "ChatGPT, Gemini",
            ),
            t(
                "p0-6",
                6,
                US,
                "2 Oct 2026",
                "Done",
                "Record the starting point on the Overview scoreboard",
                "Every Baseline cell on the Overview filled with a value and a date.",
                "2 Oct: all Baseline cells filled.",
            ),
        ],
    },
    {
        key: "M1",
        name: "M1 Build and Launch",
        title: "M1 Build and Launch",
        sub: "30 Sep to 29 Oct 2026. Outlets page, menu page, articles 1 and 2. Live Thu 29 Oct 2026.",
        tasks: [
            t("m1-1", 1, CLIENT, "5 Oct 2026", "Done", "Confirm the 5 rivals for the competitor watch", "Five named rivals.", "5 Oct: five rivals named by the client."),
            t("m1-2", 2, BOTH, "5 Oct 2026", "Done", "Confirm the priority searches and the Signature answers", "Searches and Signatures confirmed on the Article Plan.", "6 Oct: confirmed by email."),
            t(
                "m1-3",
                3,
                US,
                "6 Oct 2026",
                "Done",
                "S1 Draft the outlet list as text, grouped by state",
                "One row per outlet: name, address, phone, hours; one spelling of each.",
                "7 Oct: sent to the client as the S1 Outlet List.",
            ),
            t(
                "m1-4",
                4,
                US,
                "8 Oct 2026",
                "Done",
                "S3 Site map and links repaired: duplicates removed, broken addresses redirected",
                "Site map has no duplicates; old addresses return 301 to the right page.",
                "8 Oct: redirects live, duplicates removed.",
            ),
            t(
                "m1-5",
                5,
                CLIENT,
                "12 Oct 2026",
                "Done",
                "Client confirms the outlet details",
                "Written confirmation, or corrections applied and confirmed.",
                "13 Oct: client confirmed with two corrections, applied.",
            ),
            t(
                "m1-10",
                6,
                US,
                "23 Oct 2026",
                "In progress",
                "G1 Draft articles 1 and 2",
                "Answer-first, outlet details match the confirmed list.",
                "Moved from 12 Oct: client reply came 13 Oct. 19 Oct: drafted in the shared doc.",
                "Google Docs",
            ),
            t(
                "m1-7",
                7,
                US,
                "21 Oct 2026",
                "Done",
                "A2 Build the menu page priced and tagged",
                "Every dish priced on the page and in the markup; test passes.",
                "21 Oct: live as a private preview.",
                "Rich Results Test",
            ),
            t(
                "m1-8",
                8,
                US,
                "21 Oct 2026",
                "Done",
                "S1 Build the outlets page: every outlet as text, map untouched",
                "Every outlet readable as text in the page source; map still loads.",
                "21 Oct: built as a private preview.",
            ),
            t("m1-9", 9, CLIENT, "24 Oct 2026", "Not started", "G1 Client approves articles 1 and 2", "Written approval, or changes made and approved."),
            t("m1-11", 10, CLIENT, "24 Oct 2026", "Awaiting client", "Client approves the launch set: outlets page and menu page", "Written approval of both.", "21 Oct: sent as one approval pack."),
            t("m1-12", 11, US, "27 Oct 2026", "Not started", "G1 Publish articles 1 and 2", "Both live and in the site map."),
            t(
                "m1-13",
                12,
                US,
                "28 Oct 2026",
                "Not started",
                "G2 Site health sweep 1 of 6: broken links, indexing errors, drifted details",
                "Zero new broken links; any indexing error logged in Issues & Fixes.",
            ),
            t("m1-14", 13, US, "29 Oct 2026", "Not started", "G4 Competitor watch: 5 rivals (weekly)", "What each rival published this month logged."),
            t("m1-15", 14, US, "29 Oct 2026", "Not started", "G6 Snapshot 1 of 6: the starting point, and what went live", "One page sent; every number traces to a Result Record."),
        ],
    },
    {
        key: "M2",
        name: "M2 City Pages",
        title: "M2 City Pages",
        sub: "30 Oct to 27 Nov 2026. City pages, articles 3 and 4.",
        tasks: [
            t("m2-1", 1, US, "2 Nov 2026", "Not started", "Review the publishing order against Snapshot 1 and AI reading 1", "Next two slots confirmed or swapped, with the reason logged."),
            t("m2-2", 2, US, "12 Nov 2026", "Not started", "G1 Draft articles 3 and 4", "Answer-first; one primary search each, not shared with any live page."),
            t(
                "m2-3",
                3,
                US,
                "12 Nov 2026",
                "Not started",
                "X1 Draft the two city pages: outlets, prices, the 3 lead dishes",
                "Outlets and prices match the confirmed lists; one primary search per page.",
            ),
            t(
                "m2-4",
                4,
                US,
                "17 Nov 2026",
                "Not started",
                "G2 Site health sweep 2 of 6: broken links, indexing errors, drifted details",
                "Zero new broken links; any indexing error logged in Issues & Fixes.",
            ),
            t("m2-5", 5, CLIENT, "19 Nov 2026", "Not started", "G1 Client approves articles 3 and 4", "Written approval, or changes made and approved."),
            t("m2-6", 6, CLIENT, "19 Nov 2026", "Not started", "Client approves the city pages", "Written approval, or changes made and approved."),
            t("m2-7", 7, US, "24 Nov 2026", "Not started", "G7 AI visibility check, reading 2 of 6: the 50 questions on ChatGPT and Gemini", "Range per engine recorded next to the earlier readings."),
            t("m2-8", 8, US, "25 Nov 2026", "Not started", "G1 Publish articles 3 and 4", "Both live and in the site map; indexing requested."),
            t("m2-9", 9, US, "25 Nov 2026", "Not started", "X1 Publish the city pages", "Both live, in the site map, indexing requested."),
            t("m2-10", 10, US, "27 Nov 2026", "Not started", "G6 Snapshot 2 of 6", "One page sent; every number traces to a Result Record."),
        ],
    },
];
